import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
var DEFAULT_START_TIME_THAT_SHOULDNT_BE_USED = Number.NaN;
export var createTimingChannel = function createTimingChannel() {
  var startTimeStateContext = /*#__PURE__*/createContext(undefined);

  /**
   * Records the "start time" of whatever you're timing once this component rendered.
   */
  var _StartTimeRecorder = function StartTimeRecorder() {
    var startTimeState = useContext(startTimeStateContext);
    if (startTimeState === undefined) {
      throw new Error("Location where ".concat(_StartTimeRecorder.name, " was rendered was not wrapped in a ").concat(StartTimeProvider.name));
    }
    var setStartTime = startTimeState.setStartTime;
    useEffect(function () {
      setStartTime(Date.now());
    }, [setStartTime]);
    return null;
  };

  /**
   * Declare this around all the components that will need to retrieve the start time of
   * whatever you're timing
   */
  var StartTimeProvider = function StartTimeProvider(_ref) {
    var children = _ref.children;
    var startTimeState = useRef(DEFAULT_START_TIME_THAT_SHOULDNT_BE_USED);
    var setStartTime = useCallback(function (time) {
      startTimeState.current = time;
    }, []);
    return /*#__PURE__*/React.createElement(startTimeStateContext.Provider, {
      value: {
        startTimeState: startTimeState,
        setStartTime: setStartTime
      }
    }, children);
  };

  /**
   * Gets the start time from the nearest parent start time provider
   */
  var _useStartTimeRef = function useStartTimeRef() {
    var startTimeContextValue = useContext(startTimeStateContext);
    if (startTimeContextValue === undefined) {
      throw new Error("Location where ".concat(_useStartTimeRef.name, " was called was not wrapped in a ").concat(StartTimeProvider.name));
    }
    return startTimeContextValue.startTimeState;
  };
  return {
    StartTimeProvider: StartTimeProvider,
    StartTimeRecorder: _StartTimeRecorder,
    useStartTimeRef: _useStartTimeRef
  };
};
export var useTimeSinceStartEventDispatch = function useTimeSinceStartEventDispatch(useStartTimeRef, _ref2) {
  var useEventDispatch = _ref2.useEventDispatch;
  var startTimeRef = useStartTimeRef();
  var eventDispatch = useEventDispatch();
  var wrappedEventDispatch = useCallback(function () {
    eventDispatch(Date.now() - startTimeRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDispatch]);
  return wrappedEventDispatch;
};

/**
 * Measures the time it took for a synchronous method to complete
 */
export var timeDurationSync = function timeDurationSync(callback) {
  var startTime = performance.now();
  var timedPayload = callback();
  var endTime = performance.now();
  var duration = endTime - startTime;
  return {
    timedPayload: timedPayload,
    duration: duration
  };
};

/**
 * Measures the time between executing a method and resolving the promise resolved
 * from that method
 */
export var timeDuration = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(callback) {
    var startTime, timedPayload, endTime, duration;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          startTime = Date.now();
          _context.next = 3;
          return callback();
        case 3:
          timedPayload = _context.sent;
          endTime = Date.now();
          duration = endTime - startTime;
          return _context.abrupt("return", {
            timedPayload: timedPayload,
            duration: duration
          });
        case 7:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function timeDuration(_x) {
    return _ref3.apply(this, arguments);
  };
}();